import { VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LibraryElementTypeDto,
  type LibraryElementDto,
  type MinimumRequirementDto,
  useCreateLibraryElementMutation,
  useDeleteLibraryElementMutation,
  useUpdateLibraryElementMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { MinimumRequirementModal, type MinimumRequirementFormValues } from "./MinimumRequirementModal";
import { MinimumRequirementList } from "../sourcing-events/buyer/wizard/components/MinimumRequirementList";
import { LibrarySourceSection } from "./LibrarySourceSection";

// t('Your requirements') t('SourceMagnet standard')
// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')

interface Props {
  elements: LibraryElementDto[];
  orgName: string;
  canManageOrg: boolean;
  addRefusal?: string;
  isSystemAdmin: boolean;
}

type DrawerState =
  | { kind: "add"; global: boolean }
  | { kind: "edit"; element: LibraryElementDto; initialValues: MinimumRequirementFormValues };

interface Row {
  element: LibraryElementDto;
  value: MinimumRequirementDto;
}

const rowsOf = (elements: LibraryElementDto[]): Row[] =>
  elements.flatMap((element) => (element.content.minimum ? [{ element, value: element.content.minimum }] : []));

export const MinimumRequirementLibraryTab = ({ elements, orgName, canManageOrg, addRefusal, isSystemAdmin }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createLibraryElement, { isLoading: isCreating }] = useCreateLibraryElementMutation();
  const [updateLibraryElement, { isLoading: isUpdating }] = useUpdateLibraryElementMutation();
  const [deleteLibraryElement] = useDeleteLibraryElementMutation();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);

  const minimum = elements.filter((e) => e.content.type === LibraryElementTypeDto.Minimum);
  const orgRows = rowsOf(minimum.filter((e) => !e.owner.global));
  const globalRows = rowsOf(minimum.filter((e) => e.owner.global));

  const openAdd = (global: boolean) => {
    setDrawerKey((k) => k + 1);
    setDrawer({ kind: "add", global });
  };

  const openEdit = (element: LibraryElementDto, value: MinimumRequirementDto) => {
    setDrawerKey((k) => k + 1);
    setDrawer({
      kind: "edit",
      element,
      initialValues: {
        responseType: value.responseType,
        title: value.title,
        description: value.description,
      },
    });
  };

  const handleSubmit = async (values: Omit<MinimumRequirementDto, "id">) => {
    try {
      if (drawer?.kind === "add") {
        await createLibraryElement({
          createLibraryElementRequest: {
            content: { type: LibraryElementTypeDto.Minimum, minimum: values },
            global: drawer.global,
          },
        }).unwrap();
      } else if (drawer?.kind === "edit") {
        await updateLibraryElement({
          updateLibraryElementRequest: {
            id: drawer.element.id,
            content: {
              type: LibraryElementTypeDto.Minimum,
              minimum: { ...values, id: drawer.element.content.minimum?.id ?? undefined },
            },
            tags: drawer.element.tags,
          },
        }).unwrap();
      }
      setDrawer(null);
    } catch (e) {
      trigger(e);
    }
  };

  const handleDelete = (element: LibraryElementDto) => {
    confirmationModal.show({
      title: t("Delete requirement"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: async () => {
        try {
          await deleteLibraryElement({ id: element.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <VStack align="stretch" spacing="8">
      <LibrarySourceSection
        title={t("Your requirements")}
        scope="org"
        orgName={orgName}
        editable={canManageOrg}
        addRefusal={addRefusal}
        addLabel={t("Add requirement")}
        onAdd={() => openAdd(false)}
        isEmpty={orgRows.length === 0}
        emptyText={t("No minimum requirements of your own yet.")}
      >
        <MinimumRequirementList
          layout="framed"
          items={orgRows.map((r) => r.value)}
          readOnly={!canManageOrg}
          onAdd={() => openAdd(false)}
          onEdit={(i) => openEdit(orgRows[i].element, orgRows[i].value)}
          onDelete={(i) => handleDelete(orgRows[i].element)}
        />
      </LibrarySourceSection>

      <LibrarySourceSection
        title={t("SourceMagnet standard")}
        scope="global"
        editable={isSystemAdmin}
        addRefusal={addRefusal}
        addLabel={t("Add requirement")}
        onAdd={() => openAdd(true)}
        isEmpty={globalRows.length === 0}
        emptyText={t("No SourceMagnet standard minimum requirements yet.")}
      >
        <MinimumRequirementList
          layout="framed"
          items={globalRows.map((r) => r.value)}
          readOnly={!isSystemAdmin}
          onAdd={() => openAdd(true)}
          onEdit={(i) => openEdit(globalRows[i].element, globalRows[i].value)}
          onDelete={(i) => handleDelete(globalRows[i].element)}
        />
      </LibrarySourceSection>

      <MinimumRequirementModal
        key={drawerKey}
        isOpen={drawer !== null}
        isSaving={isCreating || isUpdating}
        mode={drawer?.kind ?? "add"}
        defaultValues={drawer?.kind === "edit" ? drawer.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawer(null)}
      />
      {confirmationModal.modal}
    </VStack>
  );
};
